import { useState } from 'react';
import { Plus, LayoutGrid, List, ChevronDown, Search, FolderPlus, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CasoFilter, CasoSort, CasoView } from '@/types/casos';
import { CreateCasoDialog } from './CreateCasoDialog';

interface CasoHeaderProps {
  filter: CasoFilter;
  onFilterChange: (filter: CasoFilter) => void;
  sort: CasoSort;
  onSortChange: (sort: CasoSort) => void;
  view: CasoView;
  onViewChange: (view: CasoView) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onCreateFolder: () => void;
  currentFolderId?: string | null;
}

const sortLabels: Record<CasoSort, string> = {
  recent: 'Mais recentes',
  oldest: 'Mais antigos',
  'a-z': 'A → Z',
  'z-a': 'Z → A',
};

export function CasoHeader({
  filter,
  onFilterChange,
  sort,
  onSortChange,
  view,
  onViewChange,
  searchQuery,
  onSearchChange,
  onCreateFolder,
  currentFolderId,
}: CasoHeaderProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-tour="caso-filters">
      <div className="flex items-center gap-3 flex-wrap">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="gap-2" data-tour="create-caso-btn">
              <Plus className="h-4 w-4" />
              Novo
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => setCreateDialogOpen(true)}>
              <Briefcase className="mr-2 h-4 w-4" />
              Novo Caso
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onCreateFolder}>
              <FolderPlus className="mr-2 h-4 w-4" />
              {currentFolderId ? 'Nova Subpasta' : 'Novo Cliente (Pasta)'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Buscar por nome ou descrição..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        <Tabs value={filter} onValueChange={(v) => onFilterChange(v as CasoFilter)}>
          <TabsList>
            <TabsTrigger value="all">Todos</TabsTrigger>
            <TabsTrigger value="featured">Destacados</TabsTrigger>
            <TabsTrigger value="shared">Compartilhados</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              {sortLabels[sort]}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onSortChange('recent')}>
              Mais recentes
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSortChange('oldest')}>
              Mais antigos
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSortChange('a-z')}>
              A → Z
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSortChange('z-a')}>
              Z → A
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CreateCasoDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} currentFolderId={currentFolderId} />
    </div>
  );
}
